'use client';
// CustomTooltip Component - Shows point details on hover (date + value per market)
import { Box, Typography } from '@mui/material';
import type { TooltipProps } from 'recharts';

interface MarketConfig {
  id: string;
  name: string;
  color: string;
  marketId: string;
}

interface CustomTooltipProps extends TooltipProps<number, string> {
  markets?: MarketConfig[];
}

// Format date from payload - supports timestamp (unix seconds) or date string
function formatDate(dateInput: string | number | undefined) {
  if (dateInput === undefined) return '';
  const date = typeof dateInput === 'number' ? new Date(dateInput * 1000) : new Date(dateInput);
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'pm' : 'am';
  const displayHours = hours % 12 || 12;
  const displayMinutes = minutes.toString().padStart(2, '0');
  return `${month} ${day}, ${year} ${displayHours}:${displayMinutes} ${ampm}`;
}

export function CustomTooltip({ active, payload, markets = [] }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const payloadData = payload[0]?.payload as Record<string, unknown> | undefined;
  if (!payloadData) return null;

  const dateToDisplay = payloadData.date ?? payloadData.timestamp;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        minWidth: 200,
        pointerEvents: 'none',
      }}
    >
      <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1 }}>
        {formatDate(dateToDisplay as string | number)}
      </Typography>
      {markets.map((market) => {
        const value = (payloadData[`market_${market.marketId}`] as number) ?? 0;
        return (
          <Box
            key={market.marketId}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              mb: 0.75,
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: market.color,
              }}
            />
            <Typography
              variant="body2"
              sx={{ fontSize: '0.875rem', fontWeight: 600, color: market.color }}
            >
              {market.name} {(value * 100).toFixed(1)}%
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
